import { DetailsConstructionItem } from '@/app/models/detailsConstruction';
import { FinancialGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useSearchDetailsConstructionQuery, useUnitConstructionMutation } from '@/app/services/detailsConstructionApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import NewEditTransferCost from '../NewEditTransferCost';
import ConstructionTable from './ConstructionTable';
import NewEditConstructionDetails from './NewEditConstructionDetails';

interface ConstructionProps {
  group: FinancialGroup;
}

function Construction(props: ConstructionProps) {
  const theme = useTheme();
  const { code } = useParams();
  const period = usePeriod();
  const { t } = useTranslation(['construction', 'common']);

  const { group } = props;

  const [drawerEdit, setDrawerEdit] = useState(false);
  const [drawerTransfer, setDrawerTransfer] = useState(false);
  const [selectConstruction, setSelectConstruction] = useState<number[]>([]);

  const { selectedFinancialSubGroup, setSelectedFinancialSubGroup } = useFinancialSubGroup();
  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode: group?.id }, { skip: !group });

  useEffect(() => {
    if (financialsubGroup && financialsubGroup.length > 0) {
      const found = financialsubGroup.find((item) => item.name === group.name) ?? financialsubGroup[0];
      setSelectedFinancialSubGroup(found);
    }
  }, [financialsubGroup, group, setSelectedFinancialSubGroup]);

  const [originConstruction, setOriginConstruction] = useState<DetailsConstructionItem | null>(null);
  const [originConstructionSearch, setOriginConstructionSearch] = useState('');

  const { data: construction } = useSearchDetailsConstructionQuery(
    {
      period: period.selectedPeriod?.date as string,
      project: code || '',
      subGroup: selectedFinancialSubGroup?.id as number,
      searchTerm: originConstructionSearch || '',
    },
    {
      skip: !period.selectedPeriod?.date || !selectedFinancialSubGroup,
    },
  );

  const [selectConstructionItem, setSelectedConstructionItem] = useState<DetailsConstructionItem>();

  const [selectedItem, setSelectedItem] = useState<TransferDetailBase | undefined>();

  const { selectedFinancialGroup } = useFinancialGroup();

  useEffect(() => {
    if (!selectConstructionItem || !selectedFinancialSubGroup || !period.selectedPeriod) return;

    setSelectedItem({
      id: selectConstructionItem.id,
      originFinancialGroupCode: selectedFinancialGroup?.code as string,
      financialSubGroupId: selectedFinancialSubGroup.id,
      period: period.selectedPeriod.date,
      complement: selectConstructionItem.complement,
      newDetail: {
        description: selectConstructionItem.description,
        value: selectConstructionItem.value,
        supplier: selectConstructionItem.supplier || '',
      },
    });
  }, [selectConstructionItem, selectedFinancialSubGroup, period.selectedPeriod, selectedFinancialGroup]);

  const [unit] = useUnitConstructionMutation();
  const handleUnite = async () => {
    await toast.promise(
      unit({
        ids: selectConstruction,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setSelectConstruction([]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography component="p" variant="fieldHeader">
              {t('common:search')}
            </Typography>
            <GenericAutoComplete
              value={originConstruction}
              onChange={(value) => {
                setOriginConstruction(value);
              }}
              equalityTest={(option, value) => option?.supplier === value?.supplier}
              getOptionLabel={(option) => `${option?.supplier} - ${option?.supplierCode ? option?.supplierCode : ''}`}
              onSearch={(value) => {
                setOriginConstructionSearch(value);
              }}
              options={[]}
              placeholder={t('common:type.to.search') as string}
              sx={{ width: theme.spacing(80) }}
            />
          </Box>
          <Box sx={{ minWidth: theme.spacing(31) }}>
            <Typography variant="fieldHeader">
              {t('common:total.by.group')}
              <Typography>{formatterMonetaryValue(construction?.totalValue || null)}</Typography>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => handleUnite()}
              disabled={selectConstruction.length === 0}
            >
              {t('common:unite')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <ConstructionTable
        selectConstruction={selectConstruction}
        setSelectConstruction={setSelectConstruction}
        setDrawerEdit={setDrawerEdit}
        setDrawerTransfer={setDrawerTransfer}
        setSelectedConstructionItem={setSelectedConstructionItem}
        selectConstructionItem={selectConstructionItem as DetailsConstructionItem}
        constructionData={construction}
      />
      <LateralDrawer open={drawerEdit} setOpen={setDrawerEdit} position="right" size="bigger">
        <NewEditConstructionDetails
          selectItem={selectConstructionItem as DetailsConstructionItem}
          setDrawerOpen={setDrawerEdit}
        />
      </LateralDrawer>
      <LateralDrawer open={drawerTransfer} setOpen={setDrawerTransfer} position="right" size="bigger">
        <NewEditTransferCost selectedItem={selectedItem as TransferDetailBase} setDrawerOpen={setDrawerTransfer} />
      </LateralDrawer>
    </Box>
  );
}

export default Construction;
